import { Button } from 'admin/components/atoms/Buttons';
import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';
import Select from 'admin/components/atoms/Select';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { paths } from '~/javascripts/admin/schema/docs/api/admin';
import { ClearingsQueryParams } from '.';
import Radio from '../../atoms/Radio';

type MemberOrganizations =
  paths['/api/organizations']['get']['responses']['200']['content']['application/json']['rows'];

type Query = {
  status: string | null | undefined;
  claimed_organization_code: string | null | undefined;
  clearing_type: string | null | undefined;
  offset_status: string | null | undefined;
  clearing_start_date_from: moment.Moment | null | undefined;
  clearing_start_date_to: moment.Moment | null | undefined;
  closing_date_from: moment.Moment | null | undefined;
  closing_date_to: moment.Moment | null | undefined;
  page: number | undefined;
};

type Props = {
  memberOrganizations: MemberOrganizations;
  query: Query;
  onSearch: (query: Query) => void;
  onClear: () => void;
};

type FormFilterParams = Omit<ClearingsQueryParams, 'from' | 'to'> & {
  closing_date_range: {
    closing_date_from: moment.Moment | null;
    closing_date_to: moment.Moment | null;
  };
  clearing_start_date_range: {
    clearing_start_date_from: moment.Moment | null;
    clearing_start_date_to: moment.Moment | null;
  };
};

const FormWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 1fr 4fr;
    grid-template-rows: 50px;
    justify-items: left;
    align-items: center;
    margin-top: 5px;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
  }
  ${Select as any} {
    width: 300px;
  }
  ${Button} {
    margin-top: 5px;
    margin-right: 5px;
  }
  ${LabeledSelect as any} {
    margin-bottom: 10px;
  }
`;

const RadioGroupWrapper = styled.div`
  display: flex;
  gap: 12px;
`;

const RadioWrapper = styled.div`
  display: flex;
  gap: 4px;
`;

const ButtonsWrapper = styled.div``;

const AdvancedToggle = styled.button`
  background: none;
  border: none;
  color: #008800;
  cursor: pointer;
  padding: 8px 0;
  margin: 4px 0;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const AdvancedCount = styled.span`
  color: #6f6f6f;
  font-size: 12px;
`;

const AdvancedSection = styled.div`
  border-left: 2px solid #e0e0e0;
  padding-left: 12px;
  margin: 4px 0 8px;
`;

const FilterForm = ({ memberOrganizations, query, onSearch, onClear }: Props) => {
  const initialOptions: { label: string; value: string | null }[] = [
    { label: 'すべて', value: null },
  ];

  const memberOrgs =
    memberOrganizations?.map((o) => ({
      label: o.name ?? '',
      value: o.code ?? '',
    })) || [];

  const organizationOptions = initialOptions.concat(memberOrgs);

  const offsetStatusOptions: { label: string; value: string | null }[] = [
    { label: 'すべて', value: null },
    { label: '未回収', value: 'unsettled' },
    { label: '一部相殺済', value: 'partially_offset' },
    { label: '相殺済', value: 'fully_offset' },
    { label: '相殺あり', value: 'with_offset' },
  ];

  const defaultValues = {
    status: query.status ?? 'pending',
    claimed_organization_code: query.claimed_organization_code ?? null,
    clearing_type: query.clearing_type ?? null,
    offset_status: query.offset_status ?? null,
    closing_date_range: {
      closing_date_from: query.closing_date_from ?? null,
      closing_date_to: query.closing_date_to ?? null,
    },
    clearing_start_date_range: {
      clearing_start_date_from: query.clearing_start_date_from ?? null,
      clearing_start_date_to: query.clearing_start_date_to ?? null,
    },
  };

  const { handleSubmit, control, reset, watch } = useForm<FormFilterParams>({
    defaultValues,
  });

  const onSubmit = (data: FormFilterParams) => {
    const { clearing_start_date_range, closing_date_range, ...rest } = data;

    onSearch({
      ...clearing_start_date_range,
      ...closing_date_range,
      ...rest,
      page: 1,
    });
  };

  useEffect(() => {
    reset({
      ...defaultValues,
    });
  }, [query]);

  // 詳細検索（種別 / 決済加盟店 / 前回精算日 / 相殺状態）。適用中なら開いて表示する。
  const hasAdvancedApplied = !!(
    query.clearing_type ||
    query.claimed_organization_code ||
    query.offset_status ||
    query.clearing_start_date_from ||
    query.clearing_start_date_to
  );
  const [showAdvanced, setShowAdvanced] = useState(hasAdvancedApplied);

  const watched = watch();
  const advancedActiveCount = [
    watched.clearing_type,
    watched.claimed_organization_code,
    watched.offset_status,
    watched.clearing_start_date_range?.clearing_start_date_from ||
      watched.clearing_start_date_range?.clearing_start_date_to,
  ].filter(Boolean).length;

  return (
    <FormWrapper>
      {/* 1. 締め日 */}
      <Labeled labelText="締め日">
        <Controller
          name="closing_date_range"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DateTimeRange
              dateRange={{
                from: value.closing_date_from,
                to: value.closing_date_to,
              }}
              setDateRange={(range) => {
                onChange({
                  closing_date_from: range.from ? range.from.startOf('day') : null,
                  closing_date_to: range.to ? range.to.endOf('day') : null,
                });
              }}
              validTypeFrom="invalidAfterToday"
              validTypeTo="invalidAfterToday"
              dateTimeProps={{
                fromProps: {
                  showIcon: true,
                  timeFormat: false,
                  wrapperStyles: { width: '150px' },
                },
                toProps: {
                  showIcon: true,
                  timeFormat: false,
                  wrapperStyles: { width: '150px' },
                },
              }}
            />
          )}
        />
      </Labeled>

      {/* 2. 精算状態（デフォルト=未精算 / 「全て」は 'all'） */}
      <Labeled labelText="精算状態">
        <Controller
          control={control}
          name="status"
          render={({ field: { value, onChange } }) => (
            <RadioGroupWrapper>
              <RadioWrapper>
                <Radio
                  id="status_both"
                  checked={value === 'all'}
                  onChange={() => onChange('all')}
                />
                <Label htmlFor="status_both">全て</Label>
              </RadioWrapper>
              <RadioWrapper>
                <Radio
                  id="status_pending"
                  checked={value === 'pending'}
                  onChange={() => onChange('pending')}
                />
                <Label htmlFor="status_pending">未精算</Label>
              </RadioWrapper>
              <RadioWrapper>
                <Radio
                  id="status_done"
                  checked={value === 'done'}
                  onChange={() => onChange('done')}
                />
                <Label htmlFor="status_done">精算済</Label>
              </RadioWrapper>
            </RadioGroupWrapper>
          )}
        />
      </Labeled>

      <AdvancedToggle type="button" onClick={() => setShowAdvanced((v) => !v)}>
        {showAdvanced ? '▲' : '▼'} 詳細検索条件
        {advancedActiveCount > 0 && (
          <AdvancedCount>（{advancedActiveCount}件適用中）</AdvancedCount>
        )}
      </AdvancedToggle>

      {showAdvanced && (
        <AdvancedSection>
          {/* 3. 種別 */}
          <Labeled labelText="種別">
            <Controller
              control={control}
              name="clearing_type"
              render={({ field: { value, onChange } }) => (
                <RadioGroupWrapper>
                  <RadioWrapper>
                    <Radio
                      id="clearing_type_all"
                      checked={!value}
                      onChange={() => onChange(null)}
                    />
                    <Label htmlFor="clearing_type_all">全て</Label>
                  </RadioWrapper>
                  <RadioWrapper>
                    <Radio
                      id="clearing_type_payment"
                      checked={value === 'payment'}
                      onChange={() => onChange('payment')}
                    />
                    <Label htmlFor="clearing_type_payment">支払</Label>
                  </RadioWrapper>
                  <RadioWrapper>
                    <Radio
                      id="clearing_type_invoice"
                      checked={value === 'invoice'}
                      onChange={() => onChange('invoice')}
                    />
                    <Label htmlFor="clearing_type_invoice">請求</Label>
                  </RadioWrapper>
                </RadioGroupWrapper>
              )}
            />
          </Labeled>

          {/* 4. 決済加盟店 */}
          {!!memberOrganizations?.length && (
            <Controller
              name="claimed_organization_code"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange } }) => (
                <LabeledSelect
                  id="claimed_organization_code"
                  labelText="決済加盟店"
                  options={organizationOptions}
                  value={organizationOptions.find((o) => o.value === value)}
                  onChange={(v: typeof memberOrgs[number]) => onChange(v?.value)}
                />
              )}
            />
          )}

          {/* 5. 前回精算日 */}
          <Labeled labelText="前回精算日">
            <Controller
              name="clearing_start_date_range"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DateTimeRange
                  dateRange={{
                    from: value.clearing_start_date_from,
                    to: value.clearing_start_date_to,
                  }}
                  setDateRange={(range) => {
                    onChange({
                      clearing_start_date_from: range.from ? range.from.startOf('day') : null,
                      clearing_start_date_to: range.to ? range.to.endOf('day') : null,
                    });
                  }}
                  validTypeFrom="invalidAfterToday"
                  validTypeTo="invalidAfterToday"
                  dateTimeProps={{
                    fromProps: {
                      showIcon: true,
                      timeFormat: false,
                      wrapperStyles: { width: '150px' },
                    },
                    toProps: {
                      showIcon: true,
                      timeFormat: false,
                      wrapperStyles: { width: '150px' },
                    },
                  }}
                />
              )}
            />
          </Labeled>

          {/* 6. 相殺状態 */}
          <Controller
            name="offset_status"
            control={control}
            render={({ field: { value, onChange } }) => (
              <LabeledSelect
                id="offset_status"
                labelText="相殺状態"
                options={offsetStatusOptions}
                value={offsetStatusOptions.find((o) => o.value === (value ?? null))}
                onChange={(v: typeof offsetStatusOptions[number]) => onChange(v?.value ?? null)}
              />
            )}
          />
        </AdvancedSection>
      )}

      <ButtonsWrapper>
        <Button
          variant="neutral"
          onClick={() => {
            onClear();
            reset({ ...defaultValues });
          }}
        >
          クリア
        </Button>
        <Button onClick={handleSubmit(onSubmit)}>検索</Button>
      </ButtonsWrapper>
    </FormWrapper>
  );
};

export default FilterForm;
